/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import {createRouter, createWebHistory} from 'vue-router/auto'
import {setupLayouts} from 'virtual:generated-layouts'
import {routes} from 'vue-router/auto-routes'
import {useAuthenticationStore} from '@/stores/authentication.js';
import {useMessageStore} from "@/stores/message.js";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routes),
})

router.beforeEach(async (to, from, next) => {
  if (to?.query?.type === 'passwordreset') { // set password reset route
    const hash = to.query.hash ?? null;
    const userId = to.query.id ?? null;

    if (hash === null || userId === null) {
      return next({path: '/inloggen'});
    }
    else {
      return next({name: '/wachtwoord-instellen-[hash]-[userId]', params: {hash: hash, userId: userId}})
    }
  }
  else {
    const authStore = useAuthenticationStore();

    if (to.meta.requiresAuth === true) {
      try {
        const response = await fetch('/nl/api/authenticate');
        if (!response.ok) {
          authStore.logOut();
        }
      } catch (error) {
        authStore.logOut();
        return next({ path: '/inloggen' });
      }
    }

    if (to.meta.requiresAuth === false || authStore.isLoggedIn === true) {
      if (to.path === '/') { // home redirect
        if (authStore.isLoggedIn !== true) { // not logged in
          return next({path: '/inloggen'});
        }

        const mode = authStore.getMode;
        return next({path: mode === 'admin' ? '/admin' : '/client'}) // route to usergroup homepage
      }

      if (from.path !== to.path) { // prevent loop
        if (to.meta?.admin === true) { // admin pages are only for superadmin and admin
          const messageStore = useMessageStore();
          const noRightsMsg = 'U heeft geen rechten voor deze pagina. U bent herleid naar de homepagina.';

          if (authStore.isLoggedIn !== true) {
            messageStore.addMessage(noRightsMsg, 'error')
            return next('/');
          }

          const isAdmin = ['SUPERADMIN', 'ADMIN', 'BEDRIJF'].includes(authStore.getCurrentUserUsergroup) || authStore.mayViewAdmin;
          if (!isAdmin) {
            messageStore.addMessage(noRightsMsg, 'error')
            return next('/');
          }
        }

        return next();
      }
    }
    else {
      return next({path: '/inloggen'});
    }
  }

});

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    }
    else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  }
  else {
    console.error(err)
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

export default router
